import React from 'react';
import { t } from './i18n.js';

// Helper function to generate time options for night cooling (20:00 to 08:00)
const generateNightCoolingTimeOptions = () => {
  const options = [];
  
  // Evening times: 20:00 to 23:30
  for (let hour = 20; hour <= 23; hour++) {
    options.push(`${hour.toString().padStart(2, '0')}:00`);
    options.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  
  // Early morning times: 00:00 to 08:00
  for (let hour = 0; hour <= 8; hour++) {
    options.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 8) { // Don't add 08:30
      options.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }
  
  return options;
};

// Helper function to validate time sequence for night cooling
const isValidTimeSequence = (startTime, endTime) => {
  if (!startTime || !endTime) return true; // Allow empty values
  
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  // Convert to minutes for easier comparison
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  // For night cooling: start time should be >= 20:00 (evening) and end time should be <= 08:00 (morning)
  // If start is in evening (>= 20:00) and end is in morning (<= 08:00), it's valid
  const startIsEvening = startHour >= 20;
  const endIsMorning = endHour <= 8;
  
  if (startIsEvening && endIsMorning) {
    return true; // Valid night cooling period
  }
  
  // If both are in the same period (both evening or both morning), normal comparison
  if ((startIsEvening && endHour >= 20) || (endIsMorning && startHour <= 8)) {
    return endMinutes > startMinutes;
  }
  
  return false;
};

// Summer Mode Component
export const SummerModeSection = ({ summerMode, onInputChange }) => {
  if (!summerMode) return null;

  return (
    <div key={summerMode.id} className="control-section">
      <h3>{summerMode.name || t('ventilation.summerMode')}</h3>
      <label className="control-label">
        {t('common.enabled')}:
        <input
          type="checkbox"
          checked={summerMode.enabled}
          onChange={(e) => onInputChange(summerMode.id, null, 'enabled', e.target.checked)}
        />
      </label>
    </div>
  );
};

export const ManualOverridesSection = ({ control, onManualOverrideChange }) => {
    if (!control.manual_overrides) return null;
  
    const manualOverrides = Object.entries(control.manual_overrides);
    
    return (
      <div className="manual-overrides-section">
        <h4>{t('ventilation.manualOverrides')}</h4>
        <table className="table manual-override-table">
          <thead>
            <tr>
              <th>{t('ventilation.name')}</th>
              <th>{t('common.enabled')}</th>
              <th>{t('ventilation.minutes')}</th>
              <th>{t('ventilation.activeNow')}</th>
            </tr>
          </thead>
          <tbody>
            {manualOverrides.map(([key, override]) => (
              <tr key={key}>
                <td>{override.name}</td>
                <td style={{textAlign: 'center'}}>
                  <input
                    type="checkbox"
                    checked={override.enabled}
                    onChange={(e) => onManualOverrideChange(control.id, key, 'enabled', e.target.checked)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={override.minutes || ''}
                    onChange={(e) => onManualOverrideChange(control.id, key, 'minutes', e.target.value === '' ? null : parseInt(e.target.value))}
                    placeholder={t('ventilation.minutes')}
                    min="1"
                    step="1"
                  />
                </td>
                <td style={{textAlign: 'center'}}>
                  <span className={`status-indicator ${override.active ? 'active' : 'inactive'}`}>
                    {override.active ? t('ventilation.active') : t('ventilation.inactive')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };


// Scheduled Overrides Component
export const ScheduledOverridesSection = ({ control, onScheduledOverrideChange }) => {
  if (!control.scheduled_overrides) return null;

  const scheduledOverrides = Object.entries(control.scheduled_overrides);
  const timeOptions = generateNightCoolingTimeOptions();
  
  return (
    <div className="scheduled-overrides-section">
      <h4>{t('ventilation.scheduledOverrides')}</h4>
      {scheduledOverrides.map(([key, override]) => {
        // Only render night_cooling for now
        if (key !== 'night_cooling') return null;

        return (
          <div key={key} className="override-section">
            <div className="override-header">
              <span className="override-name">{override.name}</span>
              <label className="control-label">
                {t('common.enabled')}:
                <input
                  type="checkbox"
                  checked={override.enabled}
                  onChange={(e) => onScheduledOverrideChange(control.id, key, 'enabled', e.target.checked)}
                />
              </label>
            </div>

            {/* Validation warning */}
            {!isValidTimeSequence(override.start_at, override.end_at) && override.start_at && override.end_at && (
              <div className="validation-warning" style={{color: 'red', fontSize: '0.9em', marginBottom: '10px'}}>
                {t('ventilation.invalidTimeSequence')}
              </div>
            )}
            
            <table className="table scheduled-override-table">
              <thead>
                <tr>
                  <th>{t('ventilation.thermometerName')}</th>
                  <th>{t('ventilation.targetTemp')}</th>
                  <th>{t('ventilation.timeWindow')}</th>
                  <th>{t('ventilation.outdoorTempDelta')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {override.thermometer || t('ventilation.noThermometer')}
                  </td>
                  <td>
                    <input 
                      type="number" 
                      value={override.target_temp || ''} 
                      onChange={(e) => onScheduledOverrideChange(control.id, key, 'target_temp', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="°C"
                      step="0.5"
                    />
                  </td>
                  <td>
                    <div className="time-inputs">
                      <select
                        value={override.start_at || ''}
                        onChange={(e) => onScheduledOverrideChange(control.id, key, 'start_at', e.target.value)}
                        className="time-select"
                      >
                        <option value="">{t('ventilation.startTime')}</option>
                        {timeOptions.map(time => (
                          <option key={`start-${time}`} value={time}>{time}</option>
                        ))}
                      </select>
                      <span className="time-separator"> - </span>
                      <select
                        value={override.end_at || ''}
                        onChange={(e) => onScheduledOverrideChange(control.id, key, 'end_at', e.target.value)}
                        className="time-select"
                      >
                        <option value="">{t('ventilation.endTime')}</option>
                        {timeOptions.map(time => (
                          <option key={`end-${time}`} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <input 
                      type="number" 
                      value={override.outdoor_temp_delta || ''} 
                      onChange={(e) => onScheduledOverrideChange(control.id, key, 'outdoor_temp_delta', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="°C"
                      step="0.5"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );

};